import { useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pentabd/ui";

import { FileInquiryForm, FileInquiryTable } from "./components";

import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";

import {
  FileInquiryFormDataType,
  fileInquiryValidation,
} from "@validations/HRM/employee/fileInquiryValidation";
import { createFileInquiry, updateFileInquiry } from "@api/fileInquiry";
import { useFileInquiry } from "@hooks/HRM/employee/useFileInquiry";

export interface FileInquiryPropsType {
  id: number | null;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  isProfileDisabled: boolean;
}

function FileInquiry({
  id,
  setIsErrorModalOpen,
  setIsSuccessModalOpen,
  isProfileDisabled,
}: FileInquiryPropsType) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { user } = useUser();
  const { realm_roles: roles, employeeMasterId } = user || {};
  const {
    fileInquiry,
    employeeFileInquiry,
    fetchSingleFileInquiry,
    isLoading: loading,
    handleEditMode,
    isEditMode,
    reFetchFileInquiry,
  } = useFileInquiry(id?.toString() || null);

  const methods = useForm<FileInquiryFormDataType>({
    resolver: yupResolver(fileInquiryValidation),
    values: fileInquiry as FileInquiryFormDataType | undefined,
  });

  const onSubmit: SubmitHandler<FileInquiryFormDataType> = (data: any) => {
    setIsLoading(true);
    if (isEditMode) {
      updateFileInquiry(
        data?.id,
        { ...data, assigneeId: id, assignerId: Number(employeeMasterId) },
        (response) => {
          setIsLoading(false);
          if (response.status === "success") {
            setIsSuccessModalOpen(true);
          } else {
            setIsErrorModalOpen(true);
          }
          setIsLoading(false);
          methods.reset();
          handleEditMode(false);
          reFetchFileInquiry(id?.toString() || "");
        },
      );
    } else {
      createFileInquiry(
        {
          ...data,
          assigneeId: id,
          assignerId: Number(employeeMasterId),
        },
        (response) => {
          if (response.status === "success") {
            setIsSuccessModalOpen(true);
            methods.reset();
          } else {
            setIsErrorModalOpen(true);
          }
          setIsLoading(false);
          handleEditMode(false);
          reFetchFileInquiry(id?.toString() || "");
          methods.reset();
        },
      );
    }
  };

  return (
    <div className="d-flex flex-column gap-10">
      <FileInquiryTable
        employeeFileInquiry={employeeFileInquiry}
        fetchSingleFileInquiry={fetchSingleFileInquiry}
        isLoading={loading}
        isProfileDisabled={isProfileDisabled}
        handleEditMode={handleEditMode}
      />

      <FormProvider {...methods}>
        <form className="container" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="pt-10">
            <FileInquiryForm isEditMode={isEditMode} />
            <div className="row py-10">
              <div className="text-end col-12 col-lg-10" id="file-inquiry-form">
                <Button
                  key="file_inquiry_save"
                  htmlType="submit"
                  fill="outline"
                  type="primary"
                  size="sm"
                  loading={isLoading}
                  disabled={
                    isProfileDisabled ||
                    !getAccess(roles, "CAN_CREATE_FILE_INQUIRY")
                  }
                >
                  {t("EMPLOYEE.GENERAL_INFO.FORM.SAVE_BUTTON")}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}

export default FileInquiry;
